import { z } from "zod";

/**
 * AI queue pill contracts (PREC-3635 P3): the user-visible copywriting job list
 * (`GET /copywriting-orchestration/active`), the reviewed marker and the compact
 * company-room socket payload that keeps the pill fresh without polling.
 */

/** Lifecycle of a copywriting generation job (mirrors `list_person.copywritingStatus`). */
export const COPYWRITING_JOB_STATUSES = ["QUEUED", "RUNNING", "COMPLETED", "FAILED"] as const;

export type CopywritingJobStatus = (typeof COPYWRITING_JOB_STATUSES)[number];

export const copywritingJobStatusSchema = z.enum(COPYWRITING_JOB_STATUSES);

/** Socket action to join the company-wide generations room (queue pill). */
export const COPYWRITING_JOIN_COMPANY_GENERATIONS = "copywriting:join_company_generations";

/** Socket action carrying the compact company-room lifecycle updates. */
export const COPYWRITING_COMPANY_GENERATION_UPDATE = "copywriting:company_generation_update";

/**
 * One job of the queue pill dropdown. `FAILED` jobs never appear here (errors surface
 * per-card via the per-prospect socket); `COMPLETED` jobs appear while unreviewed and
 * generated within the recency window (48 h).
 */
export const activeGenerationJobSchema = z.object({
	listPersonId: z.coerce.number(),
	prospectId: z.coerce.number(),
	listId: z.coerce.number(),
	listName: z.string().nullable(),
	eventId: z.coerce.number().nullable(),
	status: copywritingJobStatusSchema,
	/** When the job's event row was published to the queue; null while still pending dispatch. */
	queuedAt: z.string().nullable(),
	generatedAt: z.string().nullable(),
	reviewedAt: z.string().nullable(),
	/** The tracker signal the generation was seeded from; null for unseeded runs. */
	seedSignalId: z.coerce.number().nullable(),
	/** Title of the seed signal (one-liner in the dropdown); null when unseeded. */
	seedSignalLine: z.string().nullable(),
	personName: z.string().nullable(),
	personAvatarUrl: z.string().nullable(),
	companyName: z.string().nullable(),
	/**
	 * The tracked company the lead belongs to under the feed's canonical linkage — the identity
	 * of the lead's feed card. Lets the pill find and jump to that card even when it is not on
	 * the page in view; null when the lead links to no tracked company (it has no card at all).
	 */
	trackerOrganizationId: z.coerce.number().nullable(),
});

export type ActiveGenerationJob = z.infer<typeof activeGenerationJobSchema>;

export const activeGenerationsResponseSchema = z.object({
	data: z.array(activeGenerationJobSchema),
});

export type ActiveGenerationsResponse = z.infer<typeof activeGenerationsResponseSchema>;

/**
 * Compact company-room broadcast — ids + status/stage only, never the sequence output
 * (the pill hydrates via `GET /active` on mount; sockets only keep it fresh).
 */
export const companyGenerationUpdateSchema = z.object({
	prospectId: z.coerce.number(),
	listPersonId: z.coerce.number(),
	eventId: z.coerce.number(),
	seedSignalId: z.coerce.number().nullable().optional(),
	status: copywritingJobStatusSchema,
	/** Current pipeline stage label key; only present on RUNNING stage updates. */
	stage: z.string().optional(),
	/** Error message; only present on FAILED updates. */
	error: z.string().optional(),
});

export type CompanyGenerationUpdate = z.infer<typeof companyGenerationUpdateSchema>;

/** Response of POST /copywriting-orchestration/list-persons/:listPersonId/reviewed. */
export const copywritingReviewedResponseSchema = z.object({
	listPersonId: z.coerce.number(),
	/** First-review timestamp — re-marking keeps the original value. */
	reviewedAt: z.string(),
});

export type CopywritingReviewedResponse = z.infer<typeof copywritingReviewedResponseSchema>;
